import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './CryptoSlots.css';

// Contract ABIs - MATCHED TO YOUR ACTUAL CONTRACT
const CRYPTOSLOTS_ABI = [
  // Game functions
  "function spin(uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal, uint256 currentJackpot)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8[3] reels, uint8 winType, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256 twoMatchChance, uint256 threeCherryChance, uint256 threeLemonChance, uint256 threeBellChance, uint256 threeDiamondChance, uint256 twoMatchPayout, uint256 threeCherryPayout, uint256 threeLemonPayout, uint256 threeBellPayout, uint256 threeDiamondPayout))",
  "function getDividendInfo() external view returns (address dividendContractAddress, bool isDividendEnabled)",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8[3] reels, uint8 winType, uint256 grossPayout, uint256 netPayout, bool won)",
  
  // Admin functions - READ FUNCTIONS
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function jackpotPool() external view returns (uint256)",
  
  // Admin functions - WRITE FUNCTIONS
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function updateGameConfig(tuple(uint256 noWinChance, uint256 twoMatchChance, uint256 threeCherryChance, uint256 threeLemonChance, uint256 threeBellChance, uint256 threeDiamondChance, uint256 twoMatchPayout, uint256 threeCherryPayout, uint256 threeLemonPayout, uint256 threeBellPayout, uint256 threeDiamondPayout) newConfig) external",
  "function updateWinChances(uint256 noWin, uint256 twoMatch, uint256 threeCherry, uint256 threeLemon, uint256 threeBell, uint256 threeDiamond) external",
  "function updatePayouts(uint256 twoMatch, uint256 threeCherry, uint256 threeLemon, uint256 threeBell, uint256 threeDiamond) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function fundJackpot(uint256 amount) external",
  "function emergencyWithdrawAll() external",
  "function toggleDividend(bool enabled) external",
  
  // Utility functions
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function calculateHouseEdge() external view returns (uint256)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address to, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  CRYPTOSLOTS: "0xC6D3377404F29EAb0224314cdd2988041453e3E8", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// 🎵 FUTURISTIC SOUND SYSTEM 🎵
const playSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'spin':
        createComplexSound([
          { freq: 150, duration: 2, wave: 'sawtooth', delay: 0 },
          { freq: 200, duration: 1.8, wave: 'square', delay: 100 },
          { freq: 300, duration: 1.6, wave: 'triangle', delay: 200 },
          { freq: 400, duration: 1.4, wave: 'sine', delay: 300 }
        ], 1.5);
        break;
        
      case 'jackpot':
        createComplexSound([
          { freq: 1047, duration: 0.8, wave: 'sine', delay: 0 },
          { freq: 1319, duration: 0.8, wave: 'sine', delay: 200 },
          { freq: 1568, duration: 0.8, wave: 'sine', delay: 400 },
          { freq: 2093, duration: 1.2, wave: 'sine', delay: 600 },
          { freq: 2349, duration: 1.5, wave: 'sine', delay: 800 }
        ], 0.5);
        break;
        
      case 'bigWin':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'triangle', delay: 150 },
          { freq: 784, duration: 0.5, wave: 'sine', delay: 300 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 450 }
        ], 0.3);
        break;
        
      case 'smallWin':
        createComplexSound([
          { freq: 400, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 500, duration: 0.3, wave: 'sine', delay: 100 },
          { freq: 600, duration: 0.4, wave: 'sine', delay: 200 }
        ], 0.2);
        break;
        
      case 'lose':
        createComplexSound([
          { freq: 300, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 250, duration: 0.4, wave: 'sawtooth', delay: 150 },
          { freq: 150, duration: 0.5, wave: 'sawtooth', delay: 300 }
        ], 0.2);
        break;
        
      case 'buttonHover':
        createTone(800, 0.1, 'sine');
        break;
        
      case 'buttonClick':
        createComplexSound([
          { freq: 1200, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1400, duration: 0.05, wave: 'sine', delay: 20 }
        ], 0.05);
        break;
        
      case 'reelStop':
        createComplexSound([
          { freq: 200, duration: 0.2, wave: 'square', delay: 0 },
          { freq: 400, duration: 0.15, wave: 'triangle', delay: 50 }
        ], 0.1);
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const CryptoSlots = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '1',
    isSpinning: false,
    isApproving: false,
    lastResult: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    totalWins: 0,
    showSpinPopup: false,
    reels: [0, 1, 2] // Default reel positions
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    currentJackpot: '0',
    houseEdge: '82.9',
    playersOnline: 0
  });

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminData, setAdminData] = useState({
    houseBalance: '0',
    jackpotPool: '0',
    isPaused: false,
    minBet: '1',
    maxBet: '1000000',
    dividendEnabled: false,
    gameConfig: {
      noWinChance: 8290,
      twoMatchChance: 1500,
      threeCherryChance: 200,
      threeLemonChance: 8,
      threeBellChance: 2,
      threeDiamondChance: 0,
      twoMatchPayout: 200,
      threeCherryPayout: 300,
      threeLemonPayout: 500,
      threeBellPayout: 700,
      threeDiamondPayout: 1000
    }
  });
  const [adminForm, setAdminForm] = useState({
    minBet: '1',
    maxBet: '1000000',
    fundAmount: '',
    withdrawAmount: '',
    jackpotFundAmount: '',
    dividendEnabled: false,
    // Win chances (in percentages for UI)
    noWinChance: '82.9',
    twoMatchChance: '15.0',
    threeCherryChance: '2.0',
    threeLemonChance: '0.08',
    threeBellChance: '0.02',
    threeDiamondChance: '0.0',
    // Payouts (in multipliers for UI)
    twoMatchPayout: '2.0',
    threeCherryPayout: '3.0',
    threeLemonPayout: '5.0',
    threeBellPayout: '7.0',
    threeDiamondPayout: '10.0'
  });
  const [adminLoading, setAdminLoading] = useState(false);

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [spinResult, setSpinResult] = useState(null);
  const [particles, setParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    cryptoSlots: null,
    blbToken: null
  });

  const reelRefs = [useRef(null), useRef(null), useRef(null)];
  
  // Symbol mappings
  const symbols = ['🍒', '🍋', '🍊', '🔔', '⭐', '💎'];
  const winTypeNames = ['No Win', 'Two Match', 'Three Cherry', 'Three Lemon', 'Three Bell', 'Three Diamond'];

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playSound(type, volume);
    }
  };

  // Check if current wallet is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (walletData?.account && contracts.cryptoSlots) {
        try {
          const [owner, admin] = await Promise.all([
            contracts.cryptoSlots.owner().catch(() => null),
            contracts.cryptoSlots.admin().catch(() => null)
          ]);
          
          const isOwner = owner && owner.toLowerCase() === walletData.account.toLowerCase();
          const isAdminRole = admin && admin.toLowerCase() === walletData.account.toLowerCase();
          const hasAdminAccess = isOwner || isAdminRole;
          
          setIsAdmin(hasAdminAccess);
          
          if (hasAdminAccess) {
            loadAdminData();
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      }
    };

    if (contracts.cryptoSlots) {
      checkAdminStatus();
    }
  }, [walletData?.account, contracts.cryptoSlots]);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ cryptoSlots: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const cryptoSlotsContract = new ethers.Contract(
        CONTRACT_ADDRESSES.CRYPTOSLOTS,
        CRYPTOSLOTS_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        cryptoSlots: cryptoSlotsContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadGameData(cryptoSlotsContract);
        loadBalance(blbTokenContract, cryptoSlotsContract);
      }, 500);

    } catch (error) {
      console.error('Error initializing contracts:', error);
      setContracts({ cryptoSlots: null, blbToken: null });
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.cryptoSlots) => {
    try {
      if (!contract) return;

      const [houseBalance, jackpotPool, isPaused, minBet, maxBet, config, dividendInfo] = await Promise.all([
        contract.houseBalance().catch(() => ethers.BigNumber.from(0)),
        contract.jackpotPool().catch(() => ethers.BigNumber.from(0)),
        contract.paused().catch(() => false),
        contract.minBet().catch(() => ethers.utils.parseEther('1')),
        contract.maxBet().catch(() => ethers.utils.parseEther('1000000')),
        contract.getGameConfig().catch(() => ({
          noWinChance: 8290,
          twoMatchChance: 1500,
          threeCherryChance: 200,
          threeLemonChance: 8,
          threeBellChance: 2,
          threeDiamondChance: 0,
          twoMatchPayout: 200,
          threeCherryPayout: 300,
          threeLemonPayout: 500,
          threeBellPayout: 700,
          threeDiamondPayout: 1000
        })),
        contract.getDividendInfo().catch(() => [ethers.constants.AddressZero, false])
      ]);

      const adminInfo = {
        houseBalance: ethers.utils.formatEther(houseBalance),
        jackpotPool: ethers.utils.formatEther(jackpotPool),
        isPaused,
        minBet: ethers.utils.formatEther(minBet),
        maxBet: ethers.utils.formatEther(maxBet),
        dividendEnabled: dividendInfo[1],
        gameConfig: {
          noWinChance: parseInt(config.noWinChance),
          twoMatchChance: parseInt(config.twoMatchChance),
          threeCherryChance: parseInt(config.threeCherryChance),
          threeLemonChance: parseInt(config.threeLemonChance),
          threeBellChance: parseInt(config.threeBellChance),
          threeDiamondChance: parseInt(config.threeDiamondChance),
          twoMatchPayout: parseInt(config.twoMatchPayout),
          threeCherryPayout: parseInt(config.threeCherryPayout),
          threeLemonPayout: parseInt(config.threeLemonPayout),
          threeBellPayout: parseInt(config.threeBellPayout),
          threeDiamondPayout: parseInt(config.threeDiamondPayout)
        }
      };

      setAdminData(adminInfo);
      setAdminForm({
        minBet: adminInfo.minBet,
        maxBet: adminInfo.maxBet,
        fundAmount: '',
        withdrawAmount: '',
        jackpotFundAmount: '',
        dividendEnabled: adminInfo.dividendEnabled,
        noWinChance: (adminInfo.gameConfig.noWinChance / 100).toString(),
        twoMatchChance: (adminInfo.gameConfig.twoMatchChance / 100).toString(),
        threeCherryChance: (adminInfo.gameConfig.threeCherryChance / 100).toString(),
        threeLemonChance: (adminInfo.gameConfig.threeLemonChance / 100).toString(),
        threeBellChance: (adminInfo.gameConfig.threeBellChance / 100).toString(),
        threeDiamondChance: (adminInfo.gameConfig.threeDiamondChance / 100).toString(),
        twoMatchPayout: (adminInfo.gameConfig.twoMatchPayout / 100).toString(),
        threeCherryPayout: (adminInfo.gameConfig.threeCherryPayout / 100).toString(),
        threeLemonPayout: (adminInfo.gameConfig.threeLemonPayout / 100).toString(),
        threeBellPayout: (adminInfo.gameConfig.threeBellPayout / 100).toString(),
        threeDiamondPayout: (adminInfo.gameConfig.threeDiamondPayout / 100).toString()
      });

    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Particle effects for wins
  useEffect(() => {
    if (animationState === 'result' && spinResult?.won) {
      const particleCount = spinResult.winType >= 4 ? 30 : spinResult.winType >= 2 ? 20 : 10;
      const newParticles = Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 6 + 3,
        life: 100,
        velocity: {
          x: (Math.random() - 0.5) * 4,
          y: Math.random() * -3 - 1
        }
      }));
      setParticles(newParticles);
      
      const interval = setInterval(() => {
        setParticles(prev => prev.map(p => ({ 
          ...p, 
          life: p.life - 2,
          x: p.x + p.velocity.x,
          y: p.y + p.velocity.y
        })).filter(p => p.life > 0));
      }, 50);
      
      setTimeout(() => clearInterval(interval), 5000);
    }
  }, [animationState, spinResult]);

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  // Load game data from contract
  const loadGameData = async (contract = contracts.cryptoSlots) => {
    try {
      if (!contract) {
        setGameStats({
          totalGames: 0,
          totalBets: '0',
          totalPayouts: '0',
          currentJackpot: '0',
          houseEdge: '82.9',
          playersOnline: Math.floor(Math.random() * 500) + 100
        });
        return;
      }

      let stats, recentGames, houseEdge;

      try {
        stats = await contract.getGameStats();
      } catch (e) {
        stats = {
          totalGames: ethers.BigNumber.from(0),
          totalBets: ethers.BigNumber.from(0),
          totalPayoutsAmount: ethers.BigNumber.from(0),
          currentJackpot: ethers.BigNumber.from(0)
        };
      }

      try {
        recentGames = await contract.getRecentGames(5);
      } catch (e) {
        recentGames = [];
      }

      try {
        houseEdge = await contract.calculateHouseEdge();
      } catch (e) {
        houseEdge = ethers.BigNumber.from(8290);
      }

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        currentJackpot: ethers.utils.formatEther(stats.currentJackpot),
        houseEdge: (parseInt(houseEdge.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 500) + 100
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          reels: [parseInt(game.reels[0]), parseInt(game.reels[1]), parseInt(game.reels[2])],
          winType: parseInt(game.winType),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }

    } catch (error) {
      console.log('Using default game data');
      setGameStats({
        totalGames: 0,
        totalBets: '0',
        totalPayouts: '0',
        currentJackpot: '0',
        houseEdge: '82.9',
        playersOnline: Math.floor(Math.random() * 500) + 100
      });
    }
  };

  // Load BLB balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.cryptoSlots) => {
    try {
      if (!tokenContract || !gameContract || !walletData?.account) {
        setGameState(prev => ({
          ...prev,
          balance: '1000',
          allowance: '0'
        }));
        return;
      }

      let balance, allowance;

      try {
        balance = await tokenContract.balanceOf(walletData.account);
      } catch (e) {
        balance = ethers.utils.parseEther('1000');
      }

      try {
        allowance = await tokenContract.allowance(walletData.account, gameContract.address);
      } catch (e) {
        allowance = ethers.BigNumber.from(0);
      }

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));

    } catch (error) {
      console.log('Using demo balance');
      setGameState(prev => ({
        ...prev,
        balance: '1000',
        allowance: '0'
      }));
    }
  };

  // Admin Functions
  const handlePauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const tx = await contracts.cryptoSlots.pauseGame();
        await tx.wait();
        setSuccessMessage('✅ Game paused successfully!');
      } else {
        setSuccessMessage('✅ Game paused! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to pause game: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUnpauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const tx = await contracts.cryptoSlots.unpauseGame();
        await tx.wait();
        setSuccessMessage('✅ Game unpaused successfully!');
      } else {
        setSuccessMessage('✅ Game unpaused! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to unpause game: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleFundHouse = async () => {
    try {
      if (!adminForm.fundAmount || parseFloat(adminForm.fundAmount) <= 0) {
        setErrorMessage('Please enter a valid fund amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      const fundAmountWei = ethers.utils.parseEther(adminForm.fundAmount);

      if (contracts.cryptoSlots && contracts.blbToken) {
        const allowance = await contracts.blbToken.allowance(walletData.account, contracts.cryptoSlots.address);
        if (allowance.lt(fundAmountWei)) {
          const approveTx = await contracts.blbToken.approve(contracts.cryptoSlots.address, ethers.constants.MaxUint256);
          await approveTx.wait();
        }

        const tx = await contracts.cryptoSlots.fundHouse(fundAmountWei);
        await tx.wait();
        setSuccessMessage(`✅ House funded with ${adminForm.fundAmount} BLB!`);
      } else {
        setSuccessMessage(`✅ House funded with ${adminForm.fundAmount} BLB! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, fundAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to fund house: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleFundJackpot = async () => {
    try {
      if (!adminForm.jackpotFundAmount || parseFloat(adminForm.jackpotFundAmount) <= 0) {
        setErrorMessage('Please enter a valid jackpot fund amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      const fundAmountWei = ethers.utils.parseEther(adminForm.jackpotFundAmount);

      if (contracts.cryptoSlots && contracts.blbToken) {
        const allowance = await contracts.blbToken.allowance(walletData.account, contracts.cryptoSlots.address);
        if (allowance.lt(fundAmountWei)) {
          const approveTx = await contracts.blbToken.approve(contracts.cryptoSlots.address, ethers.constants.MaxUint256);
          await approveTx.wait();
        }

        const tx = await contracts.cryptoSlots.fundJackpot(fundAmountWei);
        await tx.wait();
        setSuccessMessage(`✅ Jackpot funded with ${adminForm.jackpotFundAmount} BLB!`);
      } else {
        setSuccessMessage(`✅ Jackpot funded with ${adminForm.jackpotFundAmount} BLB! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, jackpotFundAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to fund jackpot: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleToggleDividend = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const tx = await contracts.cryptoSlots.toggleDividend(!adminForm.dividendEnabled);
        await tx.wait();
        setSuccessMessage(`✅ Dividend ${!adminForm.dividendEnabled ? 'enabled' : 'disabled'}!`);
      } else {
        setSuccessMessage(`✅ Dividend ${!adminForm.dividendEnabled ? 'enabled' : 'disabled'}! (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to toggle dividend: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateWinChances = async () => {
    try {
      const noWin = Math.round(parseFloat(adminForm.noWinChance) * 100);
      const twoMatch = Math.round(parseFloat(adminForm.twoMatchChance) * 100);
      const threeCherry = Math.round(parseFloat(adminForm.threeCherryChance) * 100);
      const threeLemon = Math.round(parseFloat(adminForm.threeLemonChance) * 100);
      const threeBell = Math.round(parseFloat(adminForm.threeBellChance) * 100);
      const threeDiamond = Math.round(parseFloat(adminForm.threeDiamondChance) * 100);

      if (noWin + twoMatch + threeCherry + threeLemon + threeBell + threeDiamond !== 10000) {
        setErrorMessage('Total chances must equal 100%');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const tx = await contracts.cryptoSlots.updateWinChances(noWin, twoMatch, threeCherry, threeLemon, threeBell, threeDiamond);
        await tx.wait();
        setSuccessMessage('✅ Win chances updated successfully!');
      } else {
        setSuccessMessage('✅ Win chances updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to update win chances: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdatePayouts = async () => {
    try {
      const twoMatch = Math.round(parseFloat(adminForm.twoMatchPayout) * 100);
      const threeCherry = Math.round(parseFloat(adminForm.threeCherryPayout) * 100);
      const threeLemon = Math.round(parseFloat(adminForm.threeLemonPayout) * 100);
      const threeBell = Math.round(parseFloat(adminForm.threeBellPayout) * 100);
      const threeDiamond = Math.round(parseFloat(adminForm.threeDiamondPayout) * 100);

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const tx = await contracts.cryptoSlots.updatePayouts(twoMatch, threeCherry, threeLemon, threeBell, threeDiamond);
        await tx.wait();
        setSuccessMessage('✅ Payouts updated successfully!');
      } else {
        setSuccessMessage('✅ Payouts updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to update payouts: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSetBetLimits = async () => {
    try {
      const minBet = parseFloat(adminForm.minBet);
      const maxBet = parseFloat(adminForm.maxBet);

      if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
        setErrorMessage('Invalid bet limits');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const minBetWei = ethers.utils.parseEther(adminForm.minBet);
        const maxBetWei = ethers.utils.parseEther(adminForm.maxBet);
        
        const tx = await contracts.cryptoSlots.setBetLimits(minBetWei, maxBetWei);
        await tx.wait();
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB`);
      } else {
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to set bet limits: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleEmergencyWithdraw = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.cryptoSlots) {
        const tx = await contracts.cryptoSlots.emergencyWithdrawAll();
        await tx.wait();
        setSuccessMessage('✅ Emergency withdrawal completed!');
      } else {
        setSuccessMessage('✅ Emergency withdrawal completed! (Demo)');
      }

      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to emergency withdraw: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  // Handle spin
  const handleSpin = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      const betAmount = parseFloat(gameState.betAmount);
      const balance = parseFloat(gameState.balance);

      if (betAmount <= 0 || betAmount > balance) {
        setErrorMessage('Invalid bet amount');
        return;
      }

      playGameSound('spin', 0.4);
      
      setGameState(prev => ({ 
        ...prev, 
        isSpinning: true, 
        showSpinPopup: true 
      }));
      setAnimationState('spinning');

      // Start reel animations
      reelRefs.forEach((reel, index) => {
        setTimeout(() => {
          if (reel.current) {
            reel.current.style.animation = `reelSpin 0.3s ease-in-out infinite`;
          }
        }, index * 300);
      });

      // If contracts available, use real blockchain
      if (contracts.cryptoSlots && contracts.blbToken) {
        try {
          const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
          const tx = await contracts.cryptoSlots.spin(betAmountWei);
          
          setSuccessMessage('Transaction submitted! Spinning reels...');
          
          const receipt = await tx.wait();
          
          const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
          
          if (gamePlayedEvent) {
            const [player, betAmount, reels, winType, grossPayout, netPayout, won] = gamePlayedEvent.args;
            
            const gameResult = {
              player,
              betAmount: ethers.utils.formatEther(betAmount),
              reels: [parseInt(reels[0]), parseInt(reels[1]), parseInt(reels[2])],
              winType: parseInt(winType),
              grossPayout: ethers.utils.formatEther(grossPayout),
              netPayout: ethers.utils.formatEther(netPayout),
              won,
              timestamp: Date.now()
            };

            setTimeout(() => {
              handleSpinResult(gameResult, true);
            }, 2000);
            
            await Promise.all([loadGameData(), loadBalance()]);
            return;
          }
        } catch (contractError) {
          console.error('Contract transaction failed:', contractError);
          
          setErrorMessage('Wallet connection low bandwidth. Refresh and try again');
          setAnimationState('idle');
          setGameState(prev => ({ 
            ...prev, 
            isSpinning: false, 
            showSpinPopup: false 
          }));
          reelRefs.forEach(reel => {
            if (reel.current) {
              reel.current.style.animation = '';
            }
          });
          return;
        }
      }

      // If no contracts available, show connection error
      setErrorMessage('Smart contract not connected. Please refresh the page and connect your wallet.');
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isSpinning: false, 
        showSpinPopup: false 
      }));
      reelRefs.forEach(reel => {
        if (reel.current) {
          reel.current.style.animation = '';
        }
      });

    } catch (error) {
      console.error('Game error:', error);
      setErrorMessage('Transaction failed. Please refresh and try again.');
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isSpinning: false, 
        showSpinPopup: false 
      }));
      reelRefs.forEach(reel => {
        if (reel.current) {
          reel.current.style.animation = '';
        }
      });
    }
  };

  const handleSpinResult = (spinResult, isReal) => {
    setSpinResult(spinResult);
    setAnimationState('result');

    // Stop reel animations and show result
    reelRefs.forEach((reel, index) => {
      setTimeout(() => {
        if (reel.current) {
          reel.current.style.animation = '';
        }
        playGameSound('reelStop', 0.3);
      }, index * 200);
    });

    // Update reel display
    setGameState(prev => ({
      ...prev,
      reels: spinResult.reels
    }));

    setTimeout(() => {
      if (spinResult.won) {
        if (spinResult.winType === 5) {
          playGameSound('jackpot', 0.8);
          setSuccessMessage(`🎰 JACKPOT! You won ${formatNumber(spinResult.netPayout)} BLB! 🎰`);
        } else if (spinResult.winType >= 3) {
          playGameSound('bigWin', 0.6);
          setSuccessMessage(`🎉 Big Win! You won ${formatNumber(spinResult.netPayout)} BLB!`);
        } else {
          playGameSound('smallWin', 0.4);
          setSuccessMessage(`🎊 You won ${formatNumber(spinResult.netPayout)} BLB!`);
        }
      } else {
        playGameSound('lose', 0.3);
        setErrorMessage(`💸 No luck this time! Lost ${formatNumber(spinResult.betAmount)} BLB`);
      }
    }, 600);

    // Update local state for real games
    if (isReal) {
      setGameState(prev => ({
        ...prev,
        isSpinning: false,
        totalWins: spinResult.won ? prev.totalWins + 1 : prev.totalWins,
        experience: prev.experience + (spinResult.won ? 100 : 20),
        playerLevel: Math.floor((prev.experience + (spinResult.won ? 100 : 20)) / 200) + 1
      }));

      setGameHistory(prev => [spinResult, ...prev.slice(0, 4)]);
    }

    // Reset after showing result
    setTimeout(() => {
      setAnimationState('idle');
      setSpinResult(null);
      setGameState(prev => ({ 
        ...prev, 
        isSpinning: false,
        showSpinPopup: false
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // Handle token approval
  const handleApprove = async () => {
    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      if (contracts.blbToken && contracts.cryptoSlots) {
        const maxAmount = ethers.constants.MaxUint256;
        const tx = await contracts.blbToken.approve(contracts.cryptoSlots.address, maxAmount);
        await tx.wait();
        await loadBalance();
        setSuccessMessage('✅ Approval successful!');
      } else {
        setGameState(prev => ({ ...prev, allowance: '999999999' }));
        setSuccessMessage('✅ Approval successful!');
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      setErrorMessage('Approval failed: ' + error.message);
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canPlay = () => {
    return connected && 
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           !needsApproval() &&
           !gameState.isSpinning &&
           !adminData.isPaused;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(0);
  };

  if (!connected) {
    return (
      <div className="cryptoslots-casino-arena">
        <div className="cryptoslots-connect-screen">
          <div className="cryptoslots-connect-icon">⚡</div>
          <h3 className="cryptoslots-connect-title">🎰 ENTER THE CASINO</h3>
          <p className="cryptoslots-connect-desc">Connect your wallet to access the CryptoSlots Casino</p>
          <button 
            className="cryptoslots-action-btn cryptoslots-spin-btn"
            onClick={() => {
              playGameSound('buttonClick', 0.4);
              setConnected(true);
              setGameState(prev => ({ ...prev, balance: '1000' }));
            }}
            onMouseEnter={() => playGameSound('buttonHover', 0.2)}
            style={{ marginTop: '2rem', width: 'auto', padding: '1rem 2rem' }}
          >
            🔗 CONNECT WALLET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cryptoslots-casino-arena">
      {/* Header */}
      <div className="cryptoslots-header-bar">
        <button 
          className="cryptoslots-back-btn" 
          onClick={() => {
            playGameSound('buttonClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('buttonHover', 0.2)}
        >
          ← EXIT CASINO
        </button>
        
        <div className="cryptoslots-title-section">
          <h2 className="cryptoslots-main-title">🎰 CRYPTOSLOTS</h2>
          <div className="cryptoslots-subtitle">
            JACKPOT: 💎 {formatNumber(gameStats.currentJackpot)} BLB
          </div>
          {adminData.isPaused && (
            <div style={{ color: '#ff4444', fontSize: '0.9rem', fontWeight: '800', marginTop: '0.3rem' }}>
              ⚠️ CASINO CLOSED
            </div>
          )}
        </div>
        
        <div className="cryptoslots-balance-display">
          <div className="cryptoslots-balance-amount">
            💎 {formatNumber(gameState.balance)} BLB
          </div>
          <div className="cryptoslots-player-level">
            LVL {gameState.playerLevel}
          </div>
          
          {isAdmin && (
            <button
              className="cryptoslots-admin-btn"
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('buttonClick', 0.3);
              }}
              title="Admin Panel"
            >
              🛡️
            </button>
          )}
          
          <button
            className="cryptoslots-sound-btn"
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('buttonClick', 0.2);
            }}
            title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      </div>

      {/* Admin Panel - Complete Casino Management */}
      {isAdmin && showAdminPanel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(13, 16, 23, 0.98) 0%, rgba(17, 24, 39, 0.95) 100%)',
            border: '3px solid #00ffff',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ color: '#00ffff', fontSize: '1.8rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>
                🛡️ CASINO ADMIN PANEL
              </h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                style={{
                  background: '#ff4444',
                  border: 'none',
                  color: 'white',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  fontWeight: '800'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {/* Casino Status */}
              <div className="cryptoslots-control-panel">
                <label className="cryptoslots-control-label">🎰 CASINO STATUS</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>
                    Status: <span style={{ color: adminData.isPaused ? '#ff4444' : '#00ff88' }}>
                      {adminData.isPaused ? '⚠️ CLOSED' : '✅ OPEN'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      className="cryptoslots-action-btn"
                      onClick={handlePauseGame}
                      disabled={adminLoading || adminData.isPaused}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        fontSize: '0.9rem',
                        background: adminData.isPaused ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #ff4444, #cc0000)',
                        borderColor: '#ff4444'
                      }}
                    >
                      ⏸️ CLOSE CASINO
                    </button>
                    <button
                      className="cryptoslots-action-btn"
                      onClick={handleUnpauseGame}
                      disabled={adminLoading || !adminData.isPaused}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        fontSize: '0.9rem',
                        background: !adminData.isPaused ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #00ff88, #00cc66)',
                        borderColor: '#00ff88'
                      }}
                    >
                      ▶️ OPEN CASINO
                    </button>
                  </div>
                </div>
              </div>

              {/* House Balance */}
              <div className="cryptoslots-control-panel">
                <label className="cryptoslots-control-label">🏦 HOUSE BALANCE</label>
                <div style={{ color: '#00ff88', fontSize: '1.5rem', fontWeight: '800', marginBottom: '1rem' }}>
                  💎 {formatNumber(adminData.houseBalance)} BLB
                </div>
                <button
                  className="cryptoslots-action-btn"
                  onClick={handleEmergencyWithdraw}
                  disabled={adminLoading}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #ff9900, #ff6600)',
                    borderColor: '#ff9900'
                  }}
                >
                  {adminLoading ? '⏳ PROCESSING...' : '🚨 EMERGENCY WITHDRAW ALL'}
                </button>
              </div>

              {/* Jackpot Pool */}
              <div className="cryptoslots-control-panel">
                <label className="cryptoslots-control-label">🎰 JACKPOT POOL</label>
                <div style={{ color: '#ffd700', fontSize: '1.5rem', fontWeight: '800', marginBottom: '1rem' }}>
                  💰 {formatNumber(adminData.jackpotPool)} BLB
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    className="cryptoslots-stake-input"
                    type="number"
                    placeholder="Amount to add to jackpot"
                    value={adminForm.jackpotFundAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, jackpotFundAmount: e.target.value }))}
                    min="1"
                    step="1"
                    style={{ marginBottom: 0 }}
                  />
                  <button
                    className="cryptoslots-action-btn"
                    onClick={handleFundJackpot}
                    disabled={adminLoading || !adminForm.jackpotFundAmount}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #ffd700, #ffaa00)',
                      borderColor: '#ffd700',
                      color: '#000'
                    }}
                  >
                    {adminLoading ? '⏳ FUNDING...' : '💰 FUND JACKPOT'}
                  </button>
                </div>
              </div>

              {/* Fund House */}
              <div className="cryptoslots-control-panel">
                <label className="cryptoslots-control-label">💰 FUND HOUSE</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    className="cryptoslots-stake-input"
                    type="number"
                    placeholder="Amount to fund house"
                    value={adminForm.fundAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, fundAmount: e.target.value }))}
                    min="1"
                    step="1"
                    style={{ marginBottom: 0 }}
                  />
                  <button
                    className="cryptoslots-action-btn"
                    onClick={handleFundHouse}
                    disabled={adminLoading || !adminForm.fundAmount}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #00ff88, #00cc66)',
                      borderColor: '#00ff88'
                    }}
                  >
                    {adminLoading ? '⏳ FUNDING...' : '💎 FUND HOUSE'}
                  </button>
                </div>
              </div>

              {/* Bet Limits */}
              <div className="cryptoslots-control-panel">
                <label className="cryptoslots-control-label">🎯 BET LIMITS</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>MIN BET</label>
                      <input
                        className="cryptoslots-stake-input"
                        type="number"
                        value={adminForm.minBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, minBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>MAX BET</label>
                      <input
                        className="cryptoslots-stake-input"
                        type="number"
                        value={adminForm.maxBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, maxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  </div>
                  <button
                    className="cryptoslots-action-btn"
                    onClick={handleSetBetLimits}
                    disabled={adminLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #0080ff, #0060cc)',
                      borderColor: '#0080ff'
                    }}
                  >
                    {adminLoading ? '⏳ UPDATING...' : '🎯 SET LIMITS'}
                  </button>
                </div>
              </div>

              {/* Dividend Settings */}
              <div className="cryptoslots-control-panel">
                <label className="cryptoslots-control-label">💸 DIVIDEND SYSTEM</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>
                    Status: <span style={{ color: adminForm.dividendEnabled ? '#00ff88' : '#ff4444' }}>
                      {adminForm.dividendEnabled ? '✅ ENABLED' : '❌ DISABLED'}
                    </span>
                  </div>
                  <button
                    className="cryptoslots-action-btn"
                    onClick={handleToggleDividend}
                    disabled={adminLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: adminForm.dividendEnabled 
                        ? 'linear-gradient(135deg, #ff4444, #cc0000)' 
                        : 'linear-gradient(135deg, #00ff88, #00cc66)',
                      borderColor: adminForm.dividendEnabled ? '#ff4444' : '#00ff88'
                    }}
                  >
                    {adminLoading ? '⏳ TOGGLING...' : (adminForm.dividendEnabled ? '❌ DISABLE DIVIDEND' : '✅ ENABLE DIVIDEND')}
                  </button>
                </div>
              </div>

              {/* Win Chances Configuration */}
              <div className="cryptoslots-control-panel" style={{ gridColumn: '1 / -1' }}>
                <label className="cryptoslots-control-label">🎲 WIN CHANCES CONFIGURATION</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      NO WIN (%)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.noWinChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, noWinChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="82.9"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      TWO MATCH (%)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.twoMatchChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, twoMatchChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="15.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      THREE 🍒 (%)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeCherryChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeCherryChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="2.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      THREE 🍋 (%)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeLemonChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeLemonChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="0.08"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      THREE 🔔 (%)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeBellChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeBellChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="0.02"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      JACKPOT 💎 (%)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeDiamondChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeDiamondChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="0.0"
                    />
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  color: 'rgba(255,255,255,0.7)', 
                  fontSize: '0.9rem', 
                  marginBottom: '1rem',
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div>
                    <strong>Total Percentage:</strong> {(
                      (parseFloat(adminForm.noWinChance || 0) + 
                       parseFloat(adminForm.twoMatchChance || 0) + 
                       parseFloat(adminForm.threeCherryChance || 0) + 
                       parseFloat(adminForm.threeLemonChance || 0) + 
                       parseFloat(adminForm.threeBellChance || 0) + 
                       parseFloat(adminForm.threeDiamondChance || 0))
                    ).toFixed(2)}%
                  </div>
                  <div>
                    <strong>House Edge:</strong> <span style={{ color: '#ffd700', fontWeight: '800' }}>{parseFloat(adminForm.noWinChance || 0).toFixed(2)}%</span>
                  </div>
                  {Math.abs((parseFloat(adminForm.noWinChance || 0) + parseFloat(adminForm.twoMatchChance || 0) + parseFloat(adminForm.threeCherryChance || 0) + parseFloat(adminForm.threeLemonChance || 0) + parseFloat(adminForm.threeBellChance || 0) + parseFloat(adminForm.threeDiamondChance || 0)) - 100) > 0.01 && (
                    <span style={{ color: '#ff4444', fontWeight: '800' }}>⚠️ Must equal 100%</span>
                  )}
                </div>

                <button
                  className="cryptoslots-action-btn"
                  onClick={handleUpdateWinChances}
                  disabled={adminLoading || Math.abs((parseFloat(adminForm.noWinChance || 0) + parseFloat(adminForm.twoMatchChance || 0) + parseFloat(adminForm.threeCherryChance || 0) + parseFloat(adminForm.threeLemonChance || 0) + parseFloat(adminForm.threeBellChance || 0) + parseFloat(adminForm.threeDiamondChance || 0)) - 100) > 0.01}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #8000ff, #6000cc)',
                    borderColor: '#8000ff'
                  }}
                >
                  {adminLoading ? '⏳ UPDATING...' : '🎲 UPDATE WIN CHANCES'}
                </button>
              </div>

              {/* Payout Configuration */}
              <div className="cryptoslots-control-panel" style={{ gridColumn: '1 / -1' }}>
                <label className="cryptoslots-control-label">💰 PAYOUT CONFIGURATION</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      TWO MATCH (x)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.twoMatchPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, twoMatchPayout: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="2.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      THREE 🍒 (x)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeCherryPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeCherryPayout: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="3.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      THREE 🍋 (x)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeLemonPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeLemonPayout: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="5.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      THREE 🔔 (x)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeBellPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeBellPayout: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="7.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      JACKPOT 💎 (x)
                    </label>
                    <input
                      className="cryptoslots-stake-input"
                      type="number"
                      value={adminForm.threeDiamondPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, threeDiamondPayout: e.target.value }))}
                      min="1"
                      max="50"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="10.0"
                    />
                  </div>
                </div>

                <button
                  className="cryptoslots-action-btn"
                  onClick={handleUpdatePayouts}
                  disabled={adminLoading}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #00ccff, #0099cc)',
                    borderColor: '#00ccff'
                  }}
                >
                  {adminLoading ? '⏳ UPDATING...' : '💰 UPDATE PAYOUTS'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {errorMessage && (
        <div className="cryptoslots-message cryptoslots-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="cryptoslots-message cryptoslots-success-msg">
          {successMessage}
        </div>
      )}

      <div className="cryptoslots-game-layout">
        {/* Main Game Zone */}
        <div className="cryptoslots-game-zone">
          {/* Slot Machine */}
          <div className="cryptoslots-machine-container">
            <div className="cryptoslots-jackpot-display">
              <div className="cryptoslots-jackpot-label">💎 MEGA JACKPOT 💎</div>
              <div className="cryptoslots-jackpot-amount">
                {formatNumber(gameStats.currentJackpot)} BLB
              </div>
            </div>

            <div className="cryptoslots-reels-container">
              {gameState.reels.map((symbolIndex, index) => (
                <div 
                  key={index}
                  ref={reelRefs[index]}
                  className={`cryptoslots-reel ${gameState.isSpinning ? 'spinning' : ''}`}
                >
                  <div className="cryptoslots-reel-symbol">
                    {symbols[symbolIndex]}
                  </div>
                </div>
              ))}
            </div>

            {/* Victory Particles */}
            {particles.map(particle => (
              <div
                key={particle.id}
                style={{
                  position: 'absolute',
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  background: `radial-gradient(circle, 
                    hsl(${Math.random() * 60 + 40}, 100%, 70%), 
                    hsl(${Math.random() * 60 + 40}, 100%, 50%))`,
                  borderRadius: '50%',
                  opacity: particle.life / 100,
                  animation: 'particleFloat 3s ease-out',
                  pointerEvents: 'none',
                  zIndex: 5,
                  boxShadow: '0 0 10px currentColor'
                }}
              />
            ))}
          </div>

          {/* Spin Popup */}
          {gameState.showSpinPopup && (
            <div className="cryptoslots-spin-popup-overlay">
              <div className="cryptoslots-spin-popup">
                <div className="cryptoslots-popup-header">
                  <h2>🎰 SPINNING THE REELS!</h2>
                  <div className="cryptoslots-bet-info">
                    <span>Stake: {formatNumber(gameState.betAmount)} BLB</span>
                    <span>Game: CRYPTOSLOTS</span>
                  </div>
                </div>

                <div className="cryptoslots-popup-reels">
                  {gameState.reels.map((symbolIndex, index) => (
                    <div 
                      key={index}
                      className={`cryptoslots-popup-reel ${
                        animationState === 'spinning' ? 'spinning-animation' : ''
                      }`}
                    >
                      {animationState === 'result' && spinResult 
                        ? symbols[spinResult.reels[index]]
                        : symbols[symbolIndex]
                      }
                    </div>
                  ))}
                </div>

                {/* Result Display in Popup */}
                {animationState === 'result' && spinResult && (
                  <div className="cryptoslots-popup-result-display">
                    <div className={`cryptoslots-popup-result-content ${
                      spinResult.won ? 'cryptoslots-popup-victory' : 'cryptoslots-popup-defeat'
                    }`}>
                      <div className="cryptoslots-popup-result-icon">
                        {spinResult.won ? (spinResult.winType === 5 ? '🎰' : '🎉') : '💸'}
                      </div>
                      <h3 className="cryptoslots-popup-result-title">
                        {spinResult.won 
                          ? (spinResult.winType === 5 ? '💎 MEGA JACKPOT! 💎' : '🎰 WINNER!') 
                          : '💸 NO WIN'
                        }
                      </h3>
                      <div className="cryptoslots-popup-result-amount">
                        {spinResult.won ? `+${formatNumber(spinResult.netPayout)}` : `-${formatNumber(spinResult.betAmount)}`} BLB
                      </div>
                      <div className="cryptoslots-popup-result-desc">
                        {spinResult.won 
                          ? `${winTypeNames[spinResult.winType]}!`
                          : 'Better luck next time!'
                        }
                      </div>
                    </div>
                  </div>
                )}

                {/* Spinning indicator */}
                {animationState === 'spinning' && (
                  <div className="cryptoslots-spinning-indicator">
                    <div className="cryptoslots-spinning-text">⚡ REELS SPINNING...</div>
                    <div className="cryptoslots-spinning-subtext">Fortune awaits, brave gambler!</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Game Controls */}
          <div className="cryptoslots-controls">
            <div className="cryptoslots-control-panel">
              <label className="cryptoslots-control-label">💰 STAKE</label>
              <div>
                <input
                  className="cryptoslots-stake-input"
                  type="number"
                  value={gameState.betAmount}
                  onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={gameState.isSpinning || adminData.isPaused}
                  min={adminData.minBet}
                  max={Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet))}
                  step="1"
                  onFocus={() => playGameSound('buttonClick', 0.1)}
                />
                <div className="cryptoslots-preset-buttons">
                  {['1', '10', '100'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('buttonClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                      disabled={gameState.isSpinning || adminData.isPaused}
                      className="cryptoslots-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('buttonClick', 0.2);
                      const maxAmount = Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet));
                      setGameState(prev => ({ ...prev, betAmount: Math.floor(maxAmount).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                    disabled={gameState.isSpinning || adminData.isPaused}
                    className="cryptoslots-preset-btn max-btn"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="cryptoslots-control-panel">
              {adminData.isPaused ? (
                <button
                  className="cryptoslots-action-btn"
                  disabled={true}
                  style={{
                    background: 'rgba(255, 68, 68, 0.3)',
                    color: '#ff4444',
                    borderColor: '#ff4444',
                    cursor: 'not-allowed'
                  }}
                >
                  ⚠️ CASINO CLOSED
                </button>
              ) : needsApproval() ? (
                <button
                  className="cryptoslots-action-btn cryptoslots-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={gameState.isApproving}
                >
                  {gameState.isApproving ? '⏳ APPROVING...' : '🔓 APPROVE BLB'}
                </button>
              ) : (
                <button
                  className="cryptoslots-action-btn cryptoslots-spin-btn"
                  onClick={handleSpin}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={!canPlay()}
                >
                  {gameState.isSpinning ? '🌪️ SPINNING...' : '🎰 SPIN REELS'}
                </button>
              )}
            </div>
          </div>

          {/* Potential Win Display */}
          {!gameState.isSpinning && !adminData.isPaused && (
            <div className="cryptoslots-potential-win">
              🎯 Play to win up to {formatNumber(gameStats.currentJackpot)} BLB Jackpot!
            </div>
          )}
        </div>

        {/* Stats Sidebar */}
        <div className="cryptoslots-stats-sidebar">
          <div className="cryptoslots-stats-panel">
            <h3 className="cryptoslots-stats-title">🏆 PLAYER STATUS</h3>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Level</span>
              <span className="cryptoslots-stats-value">{gameState.playerLevel}</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">XP</span>
              <span className="cryptoslots-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Total Wins</span>
              <span className="cryptoslots-stats-value">{gameState.totalWins}</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Rank</span>
              <span className="cryptoslots-stats-value">High Roller</span>
            </div>
          </div>

          <div className="cryptoslots-stats-panel">
            <h3 className="cryptoslots-stats-title">🎰 PAYTABLE</h3>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">🍒🍒🍒 Three Cherry</span>
              <span className="cryptoslots-stats-value">3x</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">🍋🍋🍋 Three Lemon</span>
              <span className="cryptoslots-stats-value">5x</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">🔔🔔🔔 Three Bell</span>
              <span className="cryptoslots-stats-value">7x</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">💎💎💎 JACKPOT</span>
              <span className="cryptoslots-stats-value" style={{ color: '#ffd700' }}>MEGA</span>
            </div>
          </div>

          <div className="cryptoslots-stats-panel">
            <h3 className="cryptoslots-stats-title">📊 CASINO STATS</h3>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Total Games</span>
              <span className="cryptoslots-stats-value">{formatNumber(gameStats.totalGames)}</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Total Volume</span>
              <span className="cryptoslots-stats-value">{formatNumber(gameStats.totalBets)} BLB</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Total Payouts</span>
              <span className="cryptoslots-stats-value">{formatNumber(gameStats.totalPayouts)} BLB</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">House Edge</span>
              <span className="cryptoslots-stats-value" style={{ color: '#ffd700' }}>{gameStats.houseEdge}%</span>
            </div>
            <div className="cryptoslots-stats-item">
              <span className="cryptoslots-stats-label">Players Online</span>
              <span className="cryptoslots-stats-value" style={{ color: '#00ff88' }}>{gameStats.playersOnline}</span>
            </div>
          </div>

          {gameHistory.length > 0 && (
            <div className="cryptoslots-stats-panel">
              <h3 className="cryptoslots-stats-title">📜 RECENT SPINS</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', maxHeight: '300px', overflowY: 'auto' }}>
                {gameHistory.map((game, index) => (
                  <div key={index} className="cryptoslots-history-item">
                    <div className="cryptoslots-history-reels">
                      {game.reels.map((symbolIndex, reelIndex) => (
                        <div key={reelIndex} className="cryptoslots-history-symbol">
                          {symbols[symbolIndex]}
                        </div>
                      ))}
                    </div>
                    <div className="cryptoslots-history-result">
                      <div className="cryptoslots-history-bet">
                        {formatNumber(game.betAmount)} BLB
                      </div>
                      <div className={`cryptoslots-history-payout ${
                        game.won ? 'cryptoslots-history-win' : 'cryptoslots-history-loss'
                      }`}>
                        {game.won ? `+${formatNumber(game.netPayout)}` : 'LOSS'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CryptoSlots;